import React, { ReactNode, useCallback } from 'react';

import cls from 'classnames';

import Button from '@ott/button';
import { Col, Container, Row } from '@ott/grid';
import { l10n } from '@ott/l10n';
import addScpParameterToUrl from '@ott/utility-scp-logic';

import { FooterProps, MenuItem, SeoItem, SeoItemLink } from '../../Footer';
import { MENU_ITEM_ACTIONS, PARTNERS_MENU_ITEMS } from '../../constants';
import ExtendableList from './blocks/ExtendableList';
import getMenuItems from './helpers/getMenuItems';

import styles from './CommunicationSection.scss';

const PROTOCOL_PAT = /^(http|https|\/\/)/;

const getSupportUrlByLang = (lang: string) => {
  if (lang === 'kz') {
    return 'https://support.onetwotrip.com/hc/kk';
  }

  if (lang === 'ru-by') {
    return 'https://support.onetwotrip.com/hc/be/articles/203380302-%D0%9A%D0%BE%D0%BD%D1%82%D0%B0%D0%BA%D1%82%D1%8B-OneTwoTrip?utm_source=mainMenu';
  }

  if (lang === 'ru-us') {
    return 'https://support.onetwotrip.com/hc/en-ru?utm_source=mainMenu';
  }

  return `https://support.onetwotrip.com/hc/${lang}?utm_source=mainMenu`;
};

type MenuItemAttributes = {
  target: string;
  rel?: 'nofollow';
  onClick?: () => void;
};

type Props = {
  className?: string;

  seoItems: SeoItem[];
} & Pick<
  FooterProps,
  | 'hasMenu'
  | 'hiddenMenuItems'
  | 'hostname'
  | 'isMobile'
  | 'lang'
  | 'partner'
  | 'ottBrandSupportUrl'
>;

const CommunicationSection = ({
  className,
  hasMenu,
  hiddenMenuItems,
  hostname,
  isMobile = false,
  lang,
  partner,
  seoItems,
  ottBrandSupportUrl,
}: Props) => {
  const getMenuItemLink = useCallback(
    (item: MenuItem) => {
      let url = l10n(`footer.menu.${item}.href`);

      if (hostname && !PROTOCOL_PAT.test(url)) {
        url = `${hostname}${url}`;
      }

      // для b2b ссылок, нужно прокидывать параметры c scp (если они есть в урле)
      if (['b2b', 'extranet', 'partners'].includes(item)) {
        url = addScpParameterToUrl(url);
      }

      return url;
    },
    [hostname]
  );

  const getMenuItemAttributes = (item: MenuItem) => {
    let attributes: MenuItemAttributes = {
      target: '_blank',
    };

    switch (item) {
      case 'promocode':
      case 'travelMore':
      case 'b2b':
        break;
      case 'paylater':
        attributes = {
          ...attributes,
          ...MENU_ITEM_ACTIONS.paylater,
        };
        break;
      default:
        attributes = {
          ...attributes,
          rel: 'nofollow',
        };
        break;
    }

    return attributes;
  };

  const renderMenuItem = (item: MenuItem) => {
    return (
      <a
        key={item}
        className={cls(styles.link)}
        {...getMenuItemAttributes(item)}
        href={getMenuItemLink(item)}
      >
        {l10n(`footer.menu.${item}.text`)}
      </a>
    );
  };

  const renderSeoMenuItem = (item: SeoItemLink) => {
    const attributes: MenuItemAttributes = {
      target: item.target || '_blank',
    };
    if (item.nofollow) {
      attributes.rel = 'nofollow';
    }

    return (
      <a key={item.url} {...attributes} className={cls(styles.link)} href={item.url}>
        {item.text}
      </a>
    );
  };

  const renderSeoMenu = () => {
    if (!seoItems.length) {
      return null;
    }

    const preparedSeoItems = seoItems.reduce<Props['seoItems']>((acc, curItem) => {
      if (curItem.links && curItem.links.length > 6 && !isMobile) {
        return [
          ...acc,
          {
            header: curItem.header,
            links: curItem.links.slice(0, 6),
          },
          {
            header: '',
            links: curItem.links.slice(6, 12),
          },
        ];
      } else {
        return [...acc, curItem];
      }
    }, []);

    return preparedSeoItems!.map((menuItems, i) => {
      const title = menuItems.header;

      const menuItemsArray = menuItems.links.map((item) => {
        return renderSeoMenuItem(item);
      });

      if (isMobile) {
        return (
          <ExtendableList key={i}>
            <div className={styles.titleExtendableBlock}>{title}</div>
            <Container className={styles.containerExtendableBlock}>{menuItemsArray}</Container>
          </ExtendableList>
        );
      }

      return (
        <Col key={i} desktop={2}>
          <div className={styles.title}>{title}</div>
          {menuItemsArray}
        </Col>
      );
    });
  };

  const renderMenu = () => {
    const footerItems = partner ? PARTNERS_MENU_ITEMS : getMenuItems(lang);

    const partnerItems = partner && partner.sections ? partner.sections : null;

    const hiddenItems = partnerItems || hiddenMenuItems;

    return Object.entries(footerItems).map(([category, menuItems]) => {
      const title = l10n(`footer.category.${category}`);

      const menuItemsArray = menuItems.reduce<ReactNode[]>((acc, nextItem) => {
        if (hiddenItems.includes(nextItem)) {
          return acc;
        }

        acc.push(renderMenuItem(nextItem));
        return acc;
      }, []);

      if (isMobile) {
        return (
          <ExtendableList key={category}>
            <div className={styles.titleExtendableBlock}>{title}</div>
            <Container className={styles.containerExtendableBlock}>{menuItemsArray}</Container>
          </ExtendableList>
        );
      }

      return (
        <Col key={category} desktop={2}>
          <div className={styles.title}>{title}</div>
          {menuItemsArray}
        </Col>
      );
    });
  };

  const renderContactSupportButton = () => {
    const supportUrl = ottBrandSupportUrl || getSupportUrlByLang(lang);

    if (partner) {
      return null;
    }

    return (
      <Col
        key="contactSupportButton"
        className={cls(styles.phoneContainer, styles.topSection)}
        desktop={3}
        mobile={4}
      >
        <Button
          className={styles.supportButton}
          purpose="neutral"
          size="medium"
          href={supportUrl}
          specificLocators="footer-communication-support"
        >
          {l10n('footer.contactSupport')}
        </Button>
      </Col>
    );
  };

  let supportNodes: ReactNode = renderContactSupportButton();

  const menuNode = hasMenu && (seoItems.length ? renderSeoMenu() : renderMenu());

  if (isMobile) {
    supportNodes = (
      <Container className={styles.supportPhoneButtonWrapper}>
        <div className={styles.supportWrapper}>{supportNodes}</div>
      </Container>
    );
  }

  return (
    <div className={cls(styles.container, className)}>
      {isMobile ? (
        <div>
          <div>{supportNodes}</div>
          {menuNode}
        </div>
      ) : (
        <Container>
          <Row>{supportNodes}</Row>
          <Row>{menuNode}</Row>
        </Container>
      )}
    </div>
  );
};

export default React.memo(CommunicationSection);
